import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import { mapProfile } from './socialRepo';
import { eventBus } from '../domain/events';
import { freshChannel } from '../lib/realtimeChannel';
import { base64ToArrayBuffer } from '../lib/images';
import { signalPhotoPath } from '../lib/signalPhotos';
import { reportError } from '../lib/report';
import { primaryImageUrl } from '../lib/eventImage';
import { assertCleanForMe } from './reportRepo';
import { isObjectionableError } from '../lib/contentFilter';
import type { RawSignalEvent } from '../domain/guestView';
import { mapPlanRoster, type PlanRoster } from '../lib/planRoster';
import type {
  Condition, DeclineReason, Message, Plan, Recipient, ResponseKind, Signal, SignalDraft,
  SignalEditDraft, SignalId, SignalPhoto, SignalVersion, UserId,
} from '../domain/types';

/** باکتِ private عکس‌های برنامه (مایگریشن 0141). */
const SIGNAL_PHOTO_BUCKET = 'signal-photos';
/** عمرِ آدرسِ امضاشده — یک ساعت، همان چیزی که `contactsRepo` هم می‌گیرد. */
const PHOTO_URL_TTL = 3600;

/* eslint-disable @typescript-eslint/no-explicit-any */
const mapSignal = (row: any, uid?: string): Signal => ({
  id: row.id,
  creatorId: row.creator_id,
  creator: row.creator ? mapProfile(row.creator) : undefined,
  kind: row.kind,
  title: row.title,
  note: row.note,
  categoryId: row.category_id,
  tagIds: row.signal_tags ? row.signal_tags.map((x: any) => x.tag_id) : undefined,
  timeMode: row.time_mode,
  startsAt: row.starts_at,
  endsAt: row.ends_at ?? null,
  dateOnly: row.date_only ?? false,
  approxLabel: row.approx_label,
  placeName: row.place_name,
  costNote: row.cost_note,
  event: row.event
    ? {
        id: row.event.id,
        venue: row.event.venue?.name ?? null,
        address: row.event.address ?? null,
        url: row.event.url ?? null,
        // عکسِ غیرمعتبر پنهان می‌شود، ولی آدرسش پاک نشده (مایگریشن 0135).
        imageUrl: primaryImageUrl(row.event),
        startsAt: row.event.starts_at ?? null,
        endsAt: row.event.ends_at ?? null,
      }
    : null,
  capacityMax: row.capacity_max,
  rsvpDeadline: row.rsvp_deadline,
  quorumMin: row.quorum_min,
  chainLimit: row.chain_limit,
  plan: row.plan
    ? {
        finalStartsAt: row.plan.final_starts_at,
        finalPlace: row.plan.final_place,
        outcome: row.plan.outcome,
      }
    : undefined,
  status: row.status,
  heldWords: row.held_words ?? null,
  heldAppealStatus: row.held_appeal_status ?? null,
  heldReason: row.held_reason ?? null,
  heldFix: row.held_fix ?? null,
  shareToken: row.share_token,
  createdAt: row.created_at,
  version: row.version ?? 1,
  counts: row.recipients
    ? {
        in: row.recipients.filter((r: any) => r.response === 'in').length,
        out: row.recipients.filter((r: any) => r.response === 'out').length,
        maybe: row.recipients.filter((r: any) => r.response === 'maybe').length,
        conditional: row.recipients.filter((r: any) => r.response === 'conditional').length,
        pending: row.recipients.filter((r: any) => r.response === 'pending').length,
      }
    : undefined,
  myResponse: uid
    ? row.recipients?.find((r: any) => r.recipient_id === uid)?.response
    : undefined,
  myCondition: uid
    ? (row.recipients?.find((r: any) => r.recipient_id === uid)?.condition as Condition | null)
    : undefined,
  myDeclineReason: uid
    ? (row.recipients?.find((r: any) => r.recipient_id === uid)?.decline_reason as DeclineReason | null)
    : undefined,
  myRespondedVersion: uid
    ? (row.recipients?.find((r: any) => r.recipient_id === uid)?.responded_version ?? null)
    : undefined,
});

/*
 * `event:events!…` عمداً این‌جاست و نه فقط توی دریچه‌های مهمان: سیگنالی که
 * از صفحه‌ی کشف ساخته شده، مکان و آدرسش مالِ آن رخداد است و ستون‌های خودِ
 * سیگنال خالی‌اند. تا امروز `mapSignal` ستونِ `event_id` را دور می‌ریخت،
 * پس صفحه‌ی سیگنال برای **کاربرِ حساب‌دار هم** فقط یک عنوان بود و یک
 * برچسبِ زمانِ تقریبی. مایگریشن 0081 همین را از سمتِ مهمان می‌بندد.
 */
const SIGNAL_SELECT =
  '*, creator:profiles!signals_creator_id_fkey(id,username,display_name,avatar_emoji,city,avatar_path),' +
  ' recipients:signal_recipients(recipient_id,response,condition,decline_reason,responded_version),' +
  ' signal_tags(tag_id),' +
  ' event:events!signals_event_id_fkey(id,address,url,image_url,image_invalid,starts_at,ends_at,'
  + 'venue:venues(name))';

/**
 * فید و «برنامه‌ها» برنامه‌ی قطعی‌شده را هم می‌خواهند: تقویمِ صفحه‌ی اول
 * (WHOSUP-165) باید برنامه را روی روزِ *نهایی* بنشاند، نه روزِ اولیه‌ی
 * سیگنال. `byId` و بقیه به آن نیازی ندارند و همان `SIGNAL_SELECT` را دارند.
 */
const FEED_SELECT = `${SIGNAL_SELECT}, plan:plans(final_starts_at, final_place, outcome)`;

/**
 * آمارِ پاسخ‌ها را از دیتابیس می‌گیرد و روی سیگنال‌ها می‌نشاند.
 *
 * چرا نه از رویِ آرایه‌ی `recipients`ِ همان کوئری: آن آرایه فقط ردیف‌های
 * `signal_recipients` است، و سیگنالِ «همه‌ی دوستان» ردیف ندارد — دیده شدنش
 * از راهِ `friendships` است. پس آمار مخرجش کم می‌آمد و دوستِ تازه اصلاً در
 * آن نبود. `signal_counts` از view ِ `signal_audience` می‌خواند که هر دو
 * منبع را یکی کرده. مایگریشن 0125.
 *
 * یک رفت‌وبرگشت برای کلِ فهرست، نه یکی به‌ازای هر سیگنال.
 */
async function withCounts(rows: Signal[]): Promise<Signal[]> {
  if (!rows.length) return rows;
  const { data } = await supabase.rpc('signal_counts', { p_ids: rows.map((r) => r.id) });
  type Row = {
    signal_id: string; in_n: number; out_n: number;
    maybe_n: number; conditional_n: number; pending_n: number;
  };
  const by = new Map<string, Row>(((data ?? []) as Row[]).map((c) => [c.signal_id, c]));
  for (const r of rows) {
    const c = by.get(r.id);
    if (c) {
      r.counts = {
        in: c.in_n, out: c.out_n, maybe: c.maybe_n,
        conditional: c.conditional_n, pending: c.pending_n,
      };
    }
  }
  return rows;
}

export const signalRepo = {
  /**
   * ساخت و فرستادن. اگر فیلترِ واژه‌ها گیر بدهد (WHOSUP-309)، سیگنال باز هم
   * ساخته می‌شود ولی **نگه‌داشته** (`status = 'held'`، مایگریشن 0174): برای
   * کسی نمی‌رود تا صاحبش متن را درست کند یا ادمین اعتراضش را قبول کند.
   * رابط از `status` ِ برگشتی می‌فهمد که نرفت.
   */
  async createAndSend(draft: SignalDraft, myId: string): Promise<Signal> {
    let heldWords: string[] | null = null;
    try {
      await assertCleanForMe('signal', draft.title, draft.note);
    } catch (e) {
      // دمو جایی برای نگه‌داشتن ندارد؛ همان جلوگیریِ قبلی.
      if (!isObjectionableError(e) || isDemoMode) throw e;
      heldWords = e.matched;
    }
    if (isDemoMode) {
      const mapped = demoStore.signals.createAndSend(draft, myId);
      eventBus.emit({ kind: 'signal.sent', signal: mapped, recipientCount: draft.recipientIds.length });
      return mapped;
    }
    /*
     * یک RPC، یک transaction (مایگریشن 0126). قبلاً سه insertِ جدا بود و
     * شکستِ میانی سیگنالِ نیمه‌ساخته جا می‌گذاشت — مثلاً سیگنالی که هیچ‌کس
     * درش دعوت نشده، در حالی که کاربر فکر می‌کرد فرستاده. ایمیل/پیامک عمداً
     * بیرونِ RPC می‌مانند: شکستشان نباید ساخت را برگرداند.
     *
     * «از کدام گروه آمد» همین‌جا تصمیم گرفته می‌شود، نه در دیتابیس: فقط
     * فرمِ ساخت می‌داند کاربر گروه را انتخاب کرد یا تک‌تکِ اعضا را.
     */
    const viaOf = (rid: string): { via: string; via_group_id: number | null } => {
      const g = (draft.groupPicks ?? []).find((p) => p.memberUserIds.includes(rid));
      return g ? { via: 'group', via_group_id: g.groupId } : { via: 'direct', via_group_id: null };
    };
    const { data: sig, error } = await supabase.rpc('create_signal', {
      p_draft: {
        kind: draft.kind,
        title: draft.title,
        note: draft.note ?? null,
        // اولین تگ توی ستونِ قدیمی category_id هم می‌شینه — همون قراردادِ
        // events (مایگریشن 0044). خودِ تابع این کار رو می‌کنه.
        tag_ids: draft.tagIds,
        time_mode: draft.timeMode,
        starts_at: draft.startsAt?.toISOString() ?? null,
        // بازه و «فقط روز» - مایگریشن 0057.
        ends_at: draft.endsAt?.toISOString() ?? null,
        date_only: draft.dateOnly ?? false,
        approx_label: draft.approxLabel ?? null,
        place_name: draft.placeName ?? null,
        cost_note: draft.costNote ?? null,
        chain_limit: draft.chainLimit,
        audience: draft.audience ?? 'picked',
        // ظرفیت/مهلت/نصاب — اعمالشان کارِ دیتابیس است (مایگریشن 0131).
        capacity_max: draft.capacityMax ?? null,
        rsvp_deadline: draft.rsvpDeadline?.toISOString() ?? null,
        quorum_min: draft.quorumMin ?? null,
        // رخدادی که سیگنال از روش ساخته شده - ستونش از اول توی جدول بود
        // (مایگریشن 0001) و تا مایگریشن 0081 هیچ‌وقت پر نمی‌شد.
        event_id: draft.eventId ?? null,
        // پر یعنی «نگه‌دار، نفرست» (مایگریشن 0174).
        ...(heldWords ? { held_words: heldWords } : {}),
      },
      p_recipients: draft.recipientIds.map((rid) => ({ recipient_id: rid, ...viaOf(rid) })),
    });
    if (error) throw error;
    const mapped = mapSignal(sig);
    if (mapped.status !== 'held') {
      eventBus.emit({ kind: 'signal.sent', signal: mapped, recipientCount: draft.recipientIds.length });
    }
    return mapped;
  },

  /**
   * اعتراضِ صاحبِ سیگنالِ نگه‌داشته: «این را اشتباه گرفت». به سیگنال وصل
   * می‌شود، پس قبولِ ادمین خودش سیگنال را می‌فرستد (0174).
   */
  async appealHeld(s: Signal): Promise<void> {
    if (isDemoMode) return;
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { error } = await supabase.from('content_filter_appeals').insert({
      user_id: auth.user.id,
      field: 'signal',
      body: [s.title, s.note].filter(Boolean).join('\n').slice(0, 2000) || '—',
      matched: s.heldWords ?? [],
      signal_id: s.id,
    });
    if (error) throw error;
  },

  async incoming(uid: string): Promise<Signal[]> {
    if (isDemoMode) return demoStore.signals.incoming(uid);
    const { data, error } = await supabase
      .from('signals')
      .select(FEED_SELECT)
      .neq('creator_id', uid)
      // `cancelled` عمداً این‌جاست: سیگنالی که لغو شده کسی روش پایه بوده
      // (وگرنه اصلاً حذف می‌شد و RLS از دیدش برش می‌داشت)، و اون آدم باید
      // ببینه چی شد — نه اینکه برنامه‌ش بی‌صدا از فید غیب شه.
      .in('status', ['active', 'confirmed', 'cancelled'])
      .order('created_at', { ascending: false })
      .limit(30);
    if (error) throw error;
    return (data ?? []).map((r) => mapSignal(r, uid));
  },

  async mine(uid: string): Promise<Signal[]> {
    if (isDemoMode) return demoStore.signals.mine(uid);
    const { data, error } = await supabase
      .from('signals')
      .select(FEED_SELECT)
      .eq('creator_id', uid)
      // `held`: فقط در باکسِ خودِ سازنده (0174).
      .in('status', ['active', 'confirmed', 'completed', 'cancelled', 'held'])
      .order('created_at', { ascending: false })
      .limit(30);
    if (error) throw error;
    return withCounts((data ?? []).map((r) => mapSignal(r, uid)));
  },

  /**
   * خوراکِ صفحه‌ی «برنامه‌های من» (WHOSUP-135): ساخته‌های خودم + دعوت‌هایی
   * که جوابم «نیستم» نیست (خودِ فیلترِ نمایش کارِ `planBucket` در
   * lib/planBoard.ts است — این‌جا فقط داده می‌آید). دو کوئری، چون RLSِ
   * فید همین دو مسیر را از هم جدا تعریف کرده.
   */
  async myPlans(uid: string): Promise<Signal[]> {
    if (isDemoMode) {
      return [...demoStore.signals.mine(uid), ...demoStore.signals.incoming(uid)];
    }
    const statuses = ['active', 'confirmed', 'completed', 'expired'];
    const [mine, incoming] = await Promise.all([
      supabase.from('signals').select(FEED_SELECT)
        .eq('creator_id', uid).in('status', [...statuses, 'held'])
        .order('created_at', { ascending: false }).limit(100),
      supabase.from('signals').select(FEED_SELECT)
        .neq('creator_id', uid).in('status', statuses)
        .order('created_at', { ascending: false }).limit(100),
    ]);
    if (mine.error) throw mine.error;
    if (incoming.error) throw incoming.error;
    // آمارِ پاسخ فقط روی ساخته‌های خودم — همان `withCounts`ی که `mine()` ِ
    // تبِ اولِ موبایل دارد (WHOSUP-281)؛ `signal_counts` برای بقیه چیزی نمی‌دهد.
    const own = await withCounts((mine.data ?? []).map((r) => mapSignal(r, uid)));
    return [...own, ...(incoming.data ?? []).map((r) => mapSignal(r, uid))];
  },

  async byId(id: SignalId, uid: string): Promise<Signal | null> {
    if (isDemoMode) return demoStore.signals.byId(id);
    const { data, error } = await supabase
      .from('signals').select(SIGNAL_SELECT).eq('id', id).maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const [one] = await withCounts([mapSignal(data, uid)]);
    return one;
  },

  /**
   * «این سیگنال را دیدم» — باز شدنِ صفحه‌اش (WHOSUP-136). فقط ردیفِ موجودِ
   * خودِ آدم مهر می‌خورد؛ شکستش هم مهم نیست، پس بی‌سروصدا.
   */
  markSeen(signalId: SignalId): void {
    if (isDemoMode) return;
    void supabase.rpc('mark_signal_seen', { p_signal: signalId })
      .then(() => undefined, () => undefined);
  },

  async recipients(signalId: SignalId): Promise<Recipient[]> {
    if (isDemoMode) return demoStore.signals.recipients(signalId);
    const { data, error } = await supabase
      .from('signal_recipients')
      .select('*, profile:profiles!signal_recipients_recipient_id_fkey(id,username,display_name,avatar_emoji,city,avatar_path)')
      .eq('signal_id', signalId);
    if (error) throw error;
    return (data ?? []).map((r: any) => ({
      id: r.id, signalId: r.signal_id, recipientId: r.recipient_id,
      profile: r.profile ? mapProfile(r.profile) : undefined,
      // دعوت‌شده‌ی بی‌حساب پروفایل ندارد؛ اسم و راهِ رسیدنش تنها چیزیه که داریم.
      invitedEmail: r.invited_email ?? null,
      invitedPhone: r.invited_phone ?? null,
      invitedName: r.invited_name ?? null,
      via: r.via, viaGroupId: r.via_group_id ?? null,
      seenAt: r.seen_at ?? null,
      invitedBy: r.invited_by ?? null,
      response: r.response, condition: r.condition,
      declineReason: r.decline_reason,
      respondedVersion: r.responded_version ?? null,
    }));
  },

  /**
   * ویرایشِ سیگنال — فقط سازنده (`s_upd` در دیتابیس همین رو اجرا می‌کنه).
   *
   * هیچ‌جا نسخه‌ی تازه ساخته نمی‌شه و هیچ اعلانی فرستاده نمی‌شه: هر دو کارِ
   * تریگرهای مایگریشن 0072ان. دلیلش این نیست که کوتاه‌تر می‌شه — اینه که
   * ویرایش از دو تا اپ و (روزی) از اسکریپت می‌آد، و تاریخچه‌ای که به یادِ
   * صدازننده بستگی داشته باشه، همون تاریخچه‌ایه که یه روز ناقص می‌شه.
   */
  async update(id: SignalId, draft: SignalEditDraft): Promise<Signal> {
    // فیلترِ واژه‌های ناپسند (WHOSUP-309): پیش از دمو هم، تا رفتار یکی باشد.
    // سیگنالِ نگه‌داشته (0174) استثناست: ذخیره می‌شود و نگه‌داشته می‌ماند،
    // و اگر متنش پاک شد، همین‌جا فرستاده می‌شود.
    let heldWords: string[] | null = null;
    try {
      await assertCleanForMe('signal', draft.title, draft.note);
    } catch (e) {
      if (!isObjectionableError(e) || isDemoMode) throw e;
      const { data: cur } = await supabase.from('signals').select('status').eq('id', id).maybeSingle();
      if (cur?.status !== 'held') throw e;
      heldWords = e.matched;
    }
    if (isDemoMode) return demoStore.signals.update(id, draft);
    const { data, error } = await supabase
      .from('signals')
      .update({
        ...(heldWords ? { held_words: heldWords } : {}),
        kind: draft.kind,
        title: draft.title,
        note: draft.note ?? null,
        category_id: draft.tagIds[0] ?? null,
        time_mode: draft.timeMode,
        starts_at: draft.startsAt?.toISOString() ?? null,
        ends_at: draft.endsAt?.toISOString() ?? null,
        date_only: draft.dateOnly ?? false,
        approx_label: draft.approxLabel ?? null,
        place_name: draft.placeName ?? null,
        cost_note: draft.costNote ?? null,
        capacity_max: draft.capacityMax ?? null,
        rsvp_deadline: draft.rsvpDeadline?.toISOString() ?? null,
        quorum_min: draft.quorumMin ?? null,
        chain_limit: draft.chainLimit,
      })
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;

    // جایگزینیِ کامل، نه وصله: ساده‌ترین چیزی که «همین‌هایی که الان توی
    // فرمن» رو به جدول می‌رسونه. حذف و درجِ دوباره‌ی چند ردیفِ کوچیک ارزونه.
    const { error: eDel } = await supabase.from('signal_tags')
      .delete().eq('signal_id', id);
    if (eDel) throw eDel;
    if (draft.tagIds.length) {
      const { error: eTags } = await supabase.from('signal_tags')
        .insert(draft.tagIds.map((tag_id) => ({ signal_id: id, tag_id })));
      if (eTags) throw eTags;
    }
    // نگه‌داشتنِ مدیر (0175) با ویرایش آزاد نمی‌شود — فقط با رأیِ مدیر.
    if (data.status === 'held' && !heldWords && !data.held_reason) {
      const { error: eRel } = await supabase.rpc('release_my_held_signal', { p_id: id });
      if (eRel) throw eRel;
      return { ...mapSignal(data), status: data.kind === 'definite' ? 'confirmed' : 'active', heldWords: null };
    }
    return mapSignal(data);
  },

  /**
   * برداشتنِ سیگنال. دیتابیس تصمیم می‌گیره لغو باشه یا حذف — نگاه کن به
   * `remove_signal` در مایگریشن 0072 و دلیلِ اتمی بودنش.
   *
   * `'cancelled'` یعنی کسی گفته بود «هستم» و سیگنال با نشانِ «لغو شد» می‌مونه؛
   * `'deleted'` یعنی هیچ‌کس پایه نبود و از دیدِ همه (از جمله خودِ سازنده) رفت.
   */
  async remove(id: SignalId): Promise<'cancelled' | 'deleted'> {
    if (isDemoMode) return demoStore.signals.remove(id);
    const { data, error } = await supabase.rpc('remove_signal', { p_id: id });
    if (error) throw error;
    return (data as 'cancelled' | 'deleted') ?? 'deleted';
  },

  /** همه‌ی نسخه‌ها، قدیمی‌ترین اول — یعنی همون ترتیبی که اتفاق افتادن. */
  async versions(id: SignalId): Promise<SignalVersion[]> {
    if (isDemoMode) return demoStore.signals.versions(id);
    const { data, error } = await supabase
      .from('signal_versions').select('*').eq('signal_id', id)
      .order('version', { ascending: true });
    if (error) throw error;
    return (data ?? []).map((v: any) => ({
      signalId: v.signal_id, version: v.version, kind: v.kind, title: v.title, note: v.note,
      categoryId: v.category_id, timeMode: v.time_mode, startsAt: v.starts_at, endsAt: v.ends_at,
      dateOnly: v.date_only ?? false, approxLabel: v.approx_label, placeName: v.place_name,
      costNote: v.cost_note, chainLimit: v.chain_limit, createdAt: v.created_at,
    }));
  },

  /**
   * دعوتِ آدم‌های تازه به سیگنالی که از قبل هست.
   *
   * تکراری‌ها بی‌صدا رد می‌شن (`signal_recipients` کلیدِ یکتای
   * (سیگنال، گیرنده) داره)، وگرنه یه بار دوباره انتخاب کردنِ کسی که از اول
   * دعوت بوده، کلِ ذخیره رو می‌ترکوند.
   */
  async addRecipients(id: SignalId, recipientIds: UserId[], myId: string): Promise<void> {
    if (!recipientIds.length) return;
    if (isDemoMode) return demoStore.signals.addRecipients(id, recipientIds);
    const { error } = await supabase.from('signal_recipients')
      .upsert(
        recipientIds.map((rid) => ({
          signal_id: id, recipient_id: rid, invited_by: myId, via: 'direct',
        })),
        { onConflict: 'signal_id,recipient_id', ignoreDuplicates: true },
      );
    if (error) throw error;
  },

  /**
   * «برای هر کدام از این آدم‌ها چند دعوتِ دیگر مانده؟» (WHOSUP-204)
   *
   * فقط برای فرم‌هاست تا ردیفِ پرشده را خاکستری کنند؛ خودِ سقف در دیتابیس
   * است (مایگریشن 0141). `-1` یعنی بی‌سقف — دوستیِ تاییدشده.
   *
   * یک رفت‌وبرگشت برای کلِ فهرست، نه یکی به‌ازای هر نفر: انتخابگرِ دعوت
   * می‌تواند صد ردیف داشته باشد.
   *
   * شکستش خطا نمی‌دهد و نقشه‌ی خالی برمی‌گرداند: «نمی‌دانم» یعنی فرم باز
   * می‌ماند و نرده‌ی دیتابیس سرِ جایش است — قفل شدنِ دعوت به‌خاطرِ یک کوئریِ
   * ناموفق بدتر از یک خطای احتمالیِ لحظه‌ی فرستادن است.
   */
  async inviteAllowance(userIds: UserId[]): Promise<Map<UserId, number>> {
    const out = new Map<UserId, number>();
    if (!userIds.length || isDemoMode) return out;
    const { data, error } = await supabase.rpc('invite_allowance_for', {
      p_targets: userIds,
    });
    if (error) return out;
    for (const r of (data ?? []) as { user_id: string; remaining: number }[]) {
      out.set(r.user_id as UserId, r.remaining);
    }
    return out;
  },

  /**
   * دعوتِ زنجیره‌ای — گیرنده‌ای که «هستم» گفته دوستِ خودش را می‌آورد
   * (مایگریشن 0132). سقف و عمق و دوستی و بلاک‌ها همه در دیتابیس سنجیده
   * می‌شوند؛ خروجی تعدادِ دعوت‌های واقعاً ثبت‌شده است (پنهان‌شده‌ها و
   * تکراری‌ها بی‌صدا رد می‌شوند).
   */
  async chainInvite(signalId: SignalId, recipientIds: UserId[]): Promise<number> {
    if (!recipientIds.length) return 0;
    if (isDemoMode) return 0;
    const { data, error } = await supabase.rpc('chain_invite', {
      p_signal: signalId, p_recipients: recipientIds,
    });
    if (error) throw error;
    return (data as number) ?? 0;
  },

  /**
   * پس گرفتنِ یه دعوت.
   *
   * کسی که گفته «هستم» رو نمی‌شه برداشت و این‌جا هیچ چکی براش نیست: سیاستِ
   * `r_del` توی دیتابیس ردیفش رو اصلاً پیدا نمی‌کنه، پس حذف بی‌سروصدا هیچ
   * کاری نمی‌کنه. فرمِ ویرایش هم اصلاً دکمه‌ش رو نشون نمی‌ده.
   */
  async removeRecipient(rowId: number): Promise<void> {
    if (isDemoMode) return demoStore.signals.removeRecipient(rowId);
    const { error } = await supabase.from('signal_recipients').delete().eq('id', rowId);
    if (error) throw error;
  },

  /**
   * `declineReason` فقط وقتی معنی داره که جواب `out` باشه؛ در بقیه‌ی حالت‌ها پاک میشه
   * تا از دفعه‌ی قبل ته‌نشین نمونه.
   */
  async respond(
    signalId: SignalId, uid: string, response: ResponseKind,
    condition?: Condition, declineReason?: DeclineReason,
  ) {
    if (isDemoMode) {
      demoStore.signals.respond(signalId, uid, response, condition, declineReason);
      eventBus.emit({ kind: 'response.set', signalId, response });
      return;
    }
    /*
     * از راهِ RPC، نه `update`ِ مستقیم.
     *
     * آپدیت به ردیفِ گیرنده می‌خورد — و سیگنالی که با مخاطبِ «همه‌ی دوستان»
     * ساخته شده **هیچ ردیفی ندارد**: دیده شدنش از راهِ `friendships` است.
     * پس آپدیت صفر ردیف می‌گرفت، بدونِ هیچ خطایی، و صفحه که دوباره
     * می‌خواند سؤال را از نو نشان می‌داد. کاربر هم نمی‌توانست ردیف را
     * بسازد؛ سیاستِ درج (به‌درستی) فقط دستِ سازنده است.
     *
     * تابع ردیف را می‌سازد اگر نبود، و حقِ جواب دادن را دوباره می‌سنجد.
     * مایگریشن 0124.
     */
    const { error } = await supabase.rpc('respond_to_signal', {
      p_signal: signalId,
      p_response: response,
      p_condition: condition ?? null,
      p_decline: response === 'out' ? declineReason ?? null : null,
    });
    if (error) throw error;
    eventBus.emit({ kind: 'response.set', signalId, response });
  },

  /**
   * قطعی کردن — از مایگریشن 0127 یک RPC اتمی است: plans + وضعیت + اعلانِ
   * «قطعی شد» به مخاطبان + برنامه‌ریزیِ یادآور، همه در یک transaction.
   * دو مرحله‌ی جدای قبلی (upsert + update) می‌توانست بینشان بمیرد و
   * برنامه‌ای بسازد که هنوز active است.
   */
  /**
   * شمارِ «هستم» برای مخاطبان — «۴ از ۶» و «۲ نفرِ دیگر لازم است».
   * signal_counts عمداً فقط مالِ سازنده است؛ این یکی فقط یک عدد می‌دهد و
   * حقِ دیدنش همان حقِ دیدنِ سیگنال است (مایگریشن 0131).
   */
  async inCount(signalId: SignalId): Promise<number> {
    if (isDemoMode) return 0;
    const { data, error } = await supabase.rpc('signal_in_count', { p_signal: signalId });
    if (error) throw error;
    return (data as number) ?? 0;
  },

  async confirmPlan(
    signalId: SignalId, finalStartsAt: Date, finalPlace?: string, reminderMin?: number | null,
  ): Promise<Plan> {
    if (isDemoMode) {
      const plan = demoStore.signals.confirmPlan(signalId, finalStartsAt, finalPlace);
      eventBus.emit({ kind: 'plan.confirmed', plan });
      return plan;
    }
    const { data, error } = await supabase.rpc('confirm_signal_plan', {
      p_signal: signalId,
      p_starts_at: finalStartsAt.toISOString(),
      p_place: finalPlace ?? null,
      // null یعنی «دست نزن»: بارِ اول پیش‌فرضِ دیتابیس (۱۲۰) می‌نشیند و در
      // قطعی‌کردنِ دوباره مقدارِ قبلی می‌ماند.
      p_reminder_min: reminderMin ?? null,
    });
    if (error) throw error;
    const plan: Plan = {
      signalId, finalStartsAt: data.final_starts_at, finalPlace: data.final_place,
      reminderMin: data.reminder_min, outcome: data.outcome, shareScope: data.share_scope,
    };
    eventBus.emit({ kind: 'plan.confirmed', plan });
    return plan;
  },

  async plan(signalId: SignalId): Promise<Plan | null> {
    if (isDemoMode) return demoStore.signals.plan(signalId);
    const { data } = await supabase.from('plans').select('*').eq('signal_id', signalId).maybeSingle();
    if (!data) return null;
    return {
      signalId, finalStartsAt: data.final_starts_at, finalPlace: data.final_place,
      reminderMin: data.reminder_min, outcome: data.outcome, shareScope: data.share_scope,
    };
  },

  /**
   * «چه کسانی هستند» بعد از قطعی شدن (مایگریشن 0155، WHOSUP-267). فقط برای
   * سازنده و «هستم»ها چیزی برمی‌گرداند؛ بقیه فهرستِ خالی می‌گیرند.
   */
  async planRoster(signalId: SignalId): Promise<PlanRoster> {
    if (isDemoMode) return { people: [], guests: 0 };
    const { data, error } = await supabase.rpc('plan_roster', { p_signal: signalId });
    if (error) throw error;
    return mapPlanRoster(data as any[]);
  },

  async setOutcome(signalId: SignalId, outcome: Plan['outcome'], shareScope: Plan['shareScope']) {
    if (isDemoMode) return demoStore.signals.setOutcome(signalId, outcome, shareScope);
    const { error } = await supabase
      .from('plans')
      .update({ outcome, share_scope: shareScope, outcome_at: new Date().toISOString() })
      .eq('signal_id', signalId);
    if (error) throw error;
    await supabase.from('signals')
      .update({ status: outcome === 'cancelled' ? 'cancelled' : 'completed' })
      .eq('id', signalId);
  },

  async messages(signalId: SignalId): Promise<Message[]> {
    if (isDemoMode) return demoStore.signals.messages(signalId);
    const { data, error } = await supabase
      .from('messages')
      .select('*, sender:profiles!messages_sender_id_fkey(display_name)')
      .eq('signal_id', signalId)
      .order('created_at', { ascending: true });
    if (error) throw error;
    return (data ?? []).map((m: any) => ({
      id: m.id, signalId: m.signal_id, senderId: m.sender_id,
      senderName: m.sender?.display_name, kind: m.kind, body: m.body, createdAt: m.created_at,
      heldReason: m.held_reason ?? null, heldFix: m.held_fix ?? null,
      heldAppealStatus: m.held_appeal_status ?? null,
    }));
  },

  /**
   * «درستش کردم / اشتباه است» برای پیامی که مدیر پنهان کرد (0175). قبولِ
   * مدیر پیام را دوباره برای همه نشان می‌دهد.
   */
  async appealHeldMessage(m: Message): Promise<void> {
    if (isDemoMode) return;
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { error } = await supabase.from('content_filter_appeals').insert({
      user_id: auth.user.id,
      field: 'message',
      body: (m.body || '—').slice(0, 2000),
      matched: [],
      message_id: Number(m.id),
    });
    if (error) throw error;
  },

  async sendMessage(signalId: SignalId, uid: string, body: string) {
    // فیلترِ واژه‌های ناپسند (WHOSUP-309): پیش از دمو هم، تا رفتار یکی باشد.
    await assertCleanForMe('message', body);
    if (isDemoMode) return demoStore.signals.sendMessage(signalId, uid, body);
    const { error } = await supabase
      .from('messages').insert({ signal_id: signalId, sender_id: uid, body });
    if (error) throw error;
  },

  subscribeMessages(signalId: SignalId, onNew: () => void): () => void {
    if (isDemoMode) return () => undefined;
    // نامِ کانال ثابت است، پس اشتراکِ تازه می‌تواند روی کانالِ هنوز-زنده‌ی
    // قبلی بنشیند و خطا بدهد — نگاه کن به lib/realtimeChannel.ts.
    const ch = freshChannel(supabase, `msg-${signalId}`)
      .on('postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages', filter: `signal_id=eq.${signalId}` },
        onNew)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  },

  /* ═══ عکس‌های بعد از برنامه (مایگریشن 0141) ═══ */

  /**
   * عکس‌های یک برنامه، تازه‌ترین اول.
   *
   * باکت private است، پس آدرسِ مستقیم کار نمی‌کند و همه‌ی مسیرها **یکجا**
   * امضا می‌شوند — یک درخواست برای کلِ فهرست، نه یکی به‌ازای هر عکس. همان
   * الگوی `contactsRepo.list`.
   *
   * امضا اگر شکست بخورد `url` خالی می‌ماند و صفحه جای عکس را خالی نشان
   * می‌دهد؛ کلِ سربرگ نباید سرِ یک آدرسِ امضانشده بیفتد.
   */
  async photos(signalId: SignalId): Promise<SignalPhoto[]> {
    if (isDemoMode) return demoStore.signals.photos(signalId);
    const { data, error } = await supabase
      .from('signal_photos')
      .select('*, uploader:profiles!signal_photos_uploader_id_fkey(display_name)')
      .eq('signal_id', signalId)
      .order('created_at', { ascending: false });
    if (error) throw error;

    const rows = data ?? [];
    const byPath = new Map<string, string>();
    if (rows.length) {
      const { data: signed } = await supabase.storage
        .from(SIGNAL_PHOTO_BUCKET)
        .createSignedUrls(rows.map((p: any) => p.path), PHOTO_URL_TTL);
      for (const s of signed ?? []) if (s.path && s.signedUrl) byPath.set(s.path, s.signedUrl);
    }

    return rows.map((p: any) => ({
      id: p.id, signalId: p.signal_id, uploaderId: p.uploader_id,
      uploaderName: p.uploader?.display_name, path: p.path,
      url: byPath.get(p.path) ?? null, createdAt: p.created_at,
    }));
  },

  /**
   * فرستادنِ یک عکس: اول بایت‌ها به باکت، بعد ردیفش در جدول.
   *
   * ترتیب عمدی است. اگر ردیف اول نوشته می‌شد و آپلود می‌شکست، فهرست یک
   * عکسِ همیشه-خراب پیدا می‌کرد. برعکسش بدترین حالت یک فایلِ یتیم در باکت
   * است که کسی نمی‌بیندش — پس اگر درجِ ردیف شکست، همان‌جا پاکش می‌کنیم.
   */
  async uploadPhoto(signalId: SignalId, uid: UserId, base64: string): Promise<void> {
    if (isDemoMode) return demoStore.signals.addPhoto(signalId, uid, base64);
    const path = signalPhotoPath(signalId, uid);
    const body = base64ToArrayBuffer(base64);
    const { error: upErr } = await supabase.storage
      .from(SIGNAL_PHOTO_BUCKET)
      .upload(path, body, { contentType: 'image/jpeg', upsert: false });
    if (upErr) {
      // اندازه هم لاگ می‌شود: بیشترِ شکست‌های آپلود سرِ سقفِ حجمِ باکت‌اند.
      reportError('signalRepo.uploadPhoto', upErr, { bytes: body.byteLength, path });
      throw upErr;
    }

    const { error } = await supabase
      .from('signal_photos').insert({ signal_id: signalId, uploader_id: uid, path });
    if (error) {
      await supabase.storage.from(SIGNAL_PHOTO_BUCKET).remove([path]);
      throw error;
    }
  },

  /**
   * چند عکس با هم (انداختنِ چند فایل روی صفحه، یا انتخابِ چندتایی از گالری).
   *
   * **پشتِ سر هم، نه موازی.** پنج آپلودِ هم‌زمانِ چندمگابایتی روی داده‌ی
   * موبایل هر پنج‌تا را کُند می‌کند و احتمالِ تایم‌اوتِ همه را بالا می‌برد؛
   * پشتِ سر هم یعنی عکسِ اول زود بالا می‌رود و در فهرست دیده می‌شود.
   *
   * شکستِ یکی بقیه را نمی‌خواباند: کسی که پنج عکس انداخته، «چهارتا رفت، یکی
   * نه» را می‌خواهد، نه «هیچ‌کدام». شمارش برمی‌گردد تا صفحه بتواند بگوید.
   */
  async uploadPhotos(
    signalId: SignalId, uid: UserId, base64s: string[],
  ): Promise<{ ok: number; failed: number }> {
    let ok = 0;
    let failed = 0;
    for (const b of base64s) {
      try {
        await signalRepo.uploadPhoto(signalId, uid, b);
        ok += 1;
      } catch {
        // خطای تک‌تکشان را خودِ uploadPhoto گزارش کرده.
        failed += 1;
      }
    }
    return { ok, failed };
  },

  /**
   * برداشتنِ عکس. ردیف اول، بعد فایل — برعکسِ فرستادن، و به همان دلیل:
   * فایلِ بی‌ردیف نامرئی است، ولی ردیفِ بی‌فایل یک قابِ خراب در فهرست.
   *
   * حقش را دیتابیس می‌سنجد (`sp_del`): عکسِ خودم، یا هر عکسی اگر سازنده‌ی
   * برنامه باشم.
   */
  async removePhoto(photo: Pick<SignalPhoto, 'id' | 'path'>): Promise<void> {
    if (isDemoMode) return demoStore.signals.removePhoto(photo.id);
    const { error } = await supabase.from('signal_photos').delete().eq('id', photo.id);
    if (error) throw error;
    const { error: rmErr } = await supabase.storage
      .from(SIGNAL_PHOTO_BUCKET).remove([photo.path]);
    if (rmErr) reportError('signalRepo.removePhoto', rmErr, { path: photo.path });
  },

  /** عکسِ تازه‌ی بقیه بدونِ رفرش بیاید — قرینه‌ی `subscribeMessages`. */
  subscribePhotos(signalId: SignalId, onNew: () => void): () => void {
    if (isDemoMode) return () => undefined;
    const ch = freshChannel(supabase, `photos-${signalId}`)
      .on('postgres_changes',
        {
          event: 'INSERT', schema: 'public', table: 'signal_photos',
          filter: `signal_id=eq.${signalId}`,
        },
        onNew)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  },
};

/**
 * لینکِ عمومیِ سیگنال — **فقط خواندنی** از مایگریشن 0067.
 *
 * `respond` این‌جا بود و برداشته شد، همراه با تابعِ `respond_by_token` در
 * دیتابیس. دلیلش: اسمی که مهمان تایپ می‌کرد هیچ‌جا تایید نمی‌شد، پس سازنده
 * می‌دید «یکی گفت هستم» بدونِ اینکه بداند کی، و آن جواب به هیچ ردیفی از
 * `signal_recipients` نمی‌چسبید. جوابِ معتبر فقط از لینکِ شخصی می‌آید
 * (`publicInviteRepo` در inviteRepo.ts).
 */
/**
 * چیزی که `get_signal_by_token` می‌دهد. اسم دارد چون `guestView` از رویش
 * صفحه می‌سازد و تا دیروز یک نوعِ بی‌نامِ داخلِ همین تابع بود.
 *
 * `status` می‌تواند `cancelled` هم باشد (مایگریشن 0080): لینکِ سیگنالِ
 * لغوشده زنده است و صفحه باید بگوید لغو شد، نه «لینک منقضی شده».
 */
export interface PublicSignalView {
  id: string;
  title: string;
  kind: string;
  note?: string | null;
  time_mode: string;
  starts_at?: string | null;
  ends_at?: string | null;
  date_only?: boolean;
  approx_label?: string | null;
  place_name?: string | null;
  cost_note?: string | null;
  creator_name: string;
  status?: string | null;
  /** رخدادِ کاتالوگی که سیگنال ازش زاده شده (مایگریشن 0081). */
  event?: RawSignalEvent | null;
}

export const publicSignalRepo = {
  async byToken(token: string): Promise<PublicSignalView | null> {
    if (isDemoMode) return demoStore.publicSignals.byToken(token);
    const { data, error } = await supabase.rpc('get_signal_by_token', { p_token: token });
    if (error) throw error;
    return (data as PublicSignalView | null) ?? null;
  },
};
